// src/i18n/index.js — GES-253 (41135). THE i18n RUNTIME. Set up once; every later French slice
// adds a namespace file here and changes nothing else.
//
// ── WHY react-i18next ────────────────────────────────────────────────────────────────
// 41118 §B1 measured the ground truth: this app had NO i18n framework, zero t() calls, and five
// dependencies total. react-i18next is the one addition (two packages, i18next + the React binding)
// and it was chosen for the two properties this project actually needs: a real FALLBACK CHAIN (a
// missing French key renders the English one, so a half-translated app never blanks) and NAMESPACES
// (so a slice is one JSON file per page per language, not one growing megafile).
//
// ── THE FALLBACK RULE, WHICH IS THE WHOLE SAFETY STORY ───────────────────────────────
// `en` is the BASE CATALOG (47110 A7i: a lazy chunk, enResources.js, like French) and is complete by construction: every t('…') added anywhere must have
// its English entry here in the same commit. `fr` is a partial overlay. So:
//     fr has the key  -> French
//     fr lacks it     -> English (fallbackLng)
//     neither has it  -> the key string itself, AND a console warning in development
// The third case is a bug, never a blank and never a crash — which is what makes it safe to
// translate the app page by page while people are using it.
//
// ── WHAT THIS FILE DOES NOT DO ───────────────────────────────────────────────────────
// It does NOT choose the language. The language is a PER-USER preference that arrives from
// GET /api/me (db/515: user_profiles.language) and is applied by AuthContext, which renders nothing
// translated until that language's catalog is in (47110 A7i: no flash of the wrong language). There
// is no navigator.language guess and no hardcoded 'fr': guessing a language would be exactly the kind
// of invented data this codebase refuses everywhere else. The one device-local value is the PREFETCH
// HINT (localStorage 'ui_lang_hint' = the language last applied on this device): it only decides which
// catalog chunk starts downloading at boot, in parallel with /api/me; it never decides what renders.
// An English user never downloads French; a French user downloads English only for a key missing in
// French, after idle (EN_FALLBACK_IDLE_MS), on a switch, or on the Language page.

// It also does NOT touch numbers, money or dates. 41118 §B5.3: the fr_ci formatting layer
// (formattingCache + utils/format.js) already renders those in French-Ivorian form for everyone,
// in both languages. GES-253 is TEXT ONLY.
//
// ── THE DISPLAY-vs-LOGIC-KEY FENCE (do not cross it) ─────────────────────────────────
// Some strings in this app are COMPARED, not read: operation_types.name ('Purchase', 'Production',
// 'Mortality', …), account types, statuses and the other closed sets are matched against English
// literals at ~117 sites (41118 §B5.2: 57 in server.js, 60 in src/). Those are LOGIC KEYS. They are
// never passed through t(), never renamed, and never translated in the database. When such a value
// must be SHOWN in French, it goes through a display-label lookup — the pattern
// category_constants.display_label already proves (config/categoryConstants.js) — so the stored
// value stays the English key and only the label moves. Adding a t() call to a comparison site is
// the one change that can silently break this app.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { afterPaint } from '../shell/afterPaint.mjs';

// The languages the app can actually render. Kept in step with the CHECK constraint on
// user_profiles.language (db/515) — widening one without the other is a bug.
export const SUPPORTED_LANGUAGES = ['en', 'fr'];
export const BASE_LANGUAGE = 'en';

// A page slice adds its pair to enResources.js + frResources.js and calls useTranslation('<page>'); nothing else here changes.
// The store, empty at start: loadLanguage() adds each language's catalogs to this same object (i18next keeps it as its store). The
// Language page awaits both before reading it (liveOverrides.pristineCatalog).
export const resources = {};

// Declared so i18next preloads them all; `common` stays the default, so a bare t('x') inside a
// page still resolves against the shared chrome catalog exactly as it did before this slice.
const NAMESPACES = ['common', 'nav', 'dashboard', 'activities', 'bandsD', 'dataEntry', 'reports', 'seatView', 'approvals', 'auditLog', 'lifecycleLog', 'benchmarks', 'inventoryMovements', 'priceList', 'sales', 'husbandry', 'customerCreditNote', 'avoir', 'returnReasons', 'dispatchSlip', 'dispatchPage', 'invoicing', 'docTemplate', 'delivery', 'dispatch', 'receipt', 'landed', 'actions', 'recordCard', 'eggLot', 'partnerDelivery', 'dataRefreshes', 'orderPage', 'invoicesPage', 'documentsPage', 'language'];

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: BASE_LANGUAGE,          // replaced by the user's stored preference as soon as /api/me answers
    fallbackLng: BASE_LANGUAGE,  // a missing fr key renders the English one — see the fallback rule
    ns: NAMESPACES,
    defaultNS: 'common',
    supportedLngs: SUPPORTED_LANGUAGES,
    load: 'languageOnly',        // 'fr-FR' from any future source still resolves to the 'fr' catalog
    returnNull: false,           // a null entry must not render as the word "null"
    returnEmptyString: false,    // an empty fr entry falls through to English instead of blanking
    interpolation: { escapeValue: false },   // React already escapes
    // A key missing from French first fetches the English fallback (47110: English is lazy for a French user), then re-renders.
    // A key missing from BOTH catalogs is a drift bug: loud in development, silent (rendering the key, never blank) in production.
    saveMissing: true,
    missingKeyHandler: (lngs, ns, key) => {
      if (!loads[BASE_LANGUAGE]) { englishFallback(); return; }
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[i18n] missing key "${ns}:${key}" in every catalog — add the English entry first.`);
      }
    },
  });

// 47070 B1 + 47110 A7i — each language is a lazy chunk. changeLanguage(lng) loads that chunk FIRST, then flips, so nothing renders in a
// language whose catalog is not there. Every existing caller (AuthContext, liveOverrides, tests) keeps calling i18n.changeLanguage.
const CHUNKS = { en: () => import('./enResources'), fr: () => import('./frResources') };
const loads = {};   // lng -> promise of its catalogs in the store
const addAll = (lng, cat) => {
  for (const [ns, bundle] of Object.entries(cat)) i18n.addResourceBundle(lng, ns, bundle, true, false);   // deep, keep existing keys
  i18n.emit('catalogLoaded', lng);   // liveOverrides re-applies the overrides of a language that just arrived
};
export function loadLanguage(lng) {
  if (!CHUNKS[lng]) return Promise.resolve();
  if (!loads[lng]) {
    loads[lng] = CHUNKS[lng]().then((m) => addAll(lng, m.default))
      .catch((e) => { loads[lng] = null; throw e; });   // a failed chunk fetch can be retried by the next request
  }
  return loads[lng];
}
export const loadFrench = () => loadLanguage('fr');
i18n.loadLanguage = loadLanguage;   // for liveOverrides / the Language page (liveOverrides is plain-node tested and cannot import this file)
i18n.loadFrench = loadFrench;
// jest renders synchronously in English: the base catalog is in the store from the start there (a dead branch in the production build).
if (process.env.NODE_ENV === 'test') { const en = require('./enResources').default; loads.en = Promise.resolve(); addAll('en', en); }

// The English fallback for a non-English user: a missing key fetches it, and it also comes after idle (EN_FALLBACK_IDLE_MS).
// Idle = this long after the language applied, then afterPaint (browser idle + network quiet): not with the boot reads, which a bare
// afterPaint would be (the network is quiet right after /api/me). null = English ONLY on a missing key (fr has 0 missing keys today).
const EN_FALLBACK_IDLE_MS = 10000;
function englishFallback() {
  if (loads[BASE_LANGUAGE]) return;
  loadLanguage(BASE_LANGUAGE).then(() => i18n.emit('languageChanged', i18n.language)).catch(() => {});   // re-render the fallback texts; an event, so a switch in flight is never overtaken
}
let idleQueued = false;
i18n.on('languageChanged', (lng) => {
  if (EN_FALLBACK_IDLE_MS == null || lng === BASE_LANGUAGE || loads[BASE_LANGUAGE] || idleQueued) return;
  idleQueued = true; setTimeout(() => afterPaint(englishFallback), EN_FALLBACK_IDLE_MS);
});

// The per-device prefetch hint (never the rendered language: see the header).
export const LANG_HINT_KEY = 'ui_lang_hint';
export function rememberLanguageHint(lng) { try { window.localStorage.setItem(LANG_HINT_KEY, lng); } catch (_) { /* no storage: no hint */ } }
export function prefetchHintedLanguage() {
  try {
    if (!window.localStorage.getItem('accessToken')) return;   // signed out: the login page renders no catalog text
    const hint = window.localStorage.getItem(LANG_HINT_KEY);
    loadLanguage(SUPPORTED_LANGUAGES.includes(hint) ? hint : BASE_LANGUAGE).catch(() => {});   // no hint yet: English, as before A7i
  } catch (_) { /* best effort */ }
}
if (process.env.NODE_ENV !== 'test') prefetchHintedLanguage();

const flip = i18n.changeLanguage.bind(i18n);
let wanted = BASE_LANGUAGE;
i18n.changeLanguage = async (lng, cb) => {
  const want = SUPPORTED_LANGUAGES.includes(lng) ? lng : BASE_LANGUAGE;
  wanted = want;
  try { await loadLanguage(want); } catch (e) {
    if (want === BASE_LANGUAGE) return flip(BASE_LANGUAGE, cb);   // English unreachable: the keys / defaultValues render, retried on the next switch
    try { await loadLanguage(BASE_LANGUAGE); } catch (_) { /* as above */ }
    if (wanted !== want) return i18n.t;
    wanted = BASE_LANGUAGE; return flip(BASE_LANGUAGE, cb);   // chunk unreachable: stay on the English base
  }
  if (wanted !== want) return i18n.t;   // a later request (e.g. sign-out -> en) overtook this one
  return flip(want, cb);
};

export default i18n;
